import React, { ChangeEvent, useState } from 'react'
//@ts-ignore
import { Card, Form, FormGroup } from 'reactstrap'
//@ts-ignore
import { Button, Textarea, Checkbox } from 'dsm-react-lib'
import { IconX } from '@tabler/icons-react'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
import { useChatService } from '../services/chatService'

interface ChatFeedbackModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  isPositive: boolean | null
  setFeedbackSubmitted: (highlight: boolean) => void
  threadId: string
  messageId: string
}

export const ChatFeedbackModal: React.FC<ChatFeedbackModalProps> = ({
  open,
  setOpen,
  isPositive,
  setFeedbackSubmitted,
  threadId,
  messageId
}) => {
  //@ts-ignore
  const trackChatFeedback = useTrackEvent(appInsights, EventType.ChatFeedback)
  const [message, setMessage] = useState<string>()
  const [hasConsent, setHasConsent] = useState<boolean>(false)
  const [isPostingFeedback, setIsPostingFeedback] = useState(false)

  const { sendMessageFeedback } = useChatService()
  const onClose = () => {
    setOpen(!open)
    setFeedbackSubmitted(false)
  }

  const onFeedbackSubmit = async (hasConsent: boolean, text?: string) => {
    setIsPostingFeedback(true)
    trackChatFeedback({
      messageId,
      type: isPositive ? 'like' : 'dislike',
      comment: text
    })
    const response = await sendMessageFeedback({
      isPositive,
      isConsentGiven: hasConsent,
      text,
      threadId,
      messageId
    })
    if (response) {
      setIsPostingFeedback(false)
      setFeedbackSubmitted(true)
      setOpen(false)
    }
  }

  return (
    <div className="chat-feedback-modal fixed inset-0 flex items-center justify-center z-[999]">
      <div className="absolute inset-0 bg-black/40 -z-10" />
      <div className="theme-surface theme-border-default max-w-[500px] w-full mx-auto shadow-lg border rounded-[6px] overflow-hidden">
        <div className="flex items-center pl-4 pr-2 py-2 border-b theme-border-default">
          <h4 className="subsection-header mb-0 text-left flex-1">Feedback</h4>
          <IconX
            className="cursor-pointer theme-icon-muted theme-hover-surface rounded-full transition-colors mr-1"
            size={24}
            onClick={onClose}
            data-testid="closeBtn"
          />
        </div>

        <div className={`chat-primary bg-[var(--color-primary)] text-white px-4 py-2.5`}>
          <h5 className="text-white text-center text-lg mb-0 leading-normal font-semibold">
            {isPositive
              ? `Glad to hear that you are happy with this response!`
              : `We're sorry to hear that this response didn't meet your expectations. Please provide more specific details so we can assist you better.`}
          </h5>
        </div>

        <Card className="theme-surface-muted theme-border-default mx-3 mt-2 mb-2 p-2 shadow-sg border rounded-[4px]">
          <Form className="!p-0 !m-0">
            <FormGroup className="!m-0 !p-0">
              <div className="w-full h-[110px] min-h-[110px] max-h-[110px] overflow-hidden">
                <Textarea
                  className="theme-feedback-input theme-input-text theme-surface border theme-border-default !h-[110px] !min-h-[110px] !max-h-[110px] resize-none focus:outline-none focus:ring-0 focus:shadow-none !overflow-y-auto"
                  placeholder="Type your feedback here (optional)"
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setMessage(e.target.value)
                  }
                  rows={5}
                  disabled={isPostingFeedback}
                  maxLength={200}
                  data-testid="feedback-comment"
                />
              </div>
            </FormGroup>
            <FormGroup className="!m-0 !p-0">
              <div
                className="flex items-start"
                style={{ marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                <Checkbox
                  color="primary"
                  variant="custom"
                  value={hasConsent}
                  disabled={isPostingFeedback}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setHasConsent(e.target.checked)}
                />
                <span
                  className="italic theme-text-subheading block"
                  style={{ lineHeight: '1.25rem' }}>
                  By checking this box, I agree to share all my chat messages from this chat for the
                  purpose of improving the service.
                </span>
              </div>
            </FormGroup>
          </Form>
        </Card>

        <div className="flex justify-end px-2 pb-2 pt-0 gap-1">
          <Button
            label="Cancel"
            color="danger"
            variant="outline"
            data-testid="cancelBtn"
            onClick={onClose}></Button>
          <Button
            label={isPostingFeedback ? 'Submitting...' : 'Submit'}
            color="primary"
            onClick={() => !isPostingFeedback && onFeedbackSubmit(hasConsent, message)}></Button>
        </div>
      </div>
    </div>
  )
}
